import React, { useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, FileDown, Mail, Check, ArrowLeft, Plus, Trash2 } from "lucide-react";

const fmt = (v, cur = "CHF") => new Intl.NumberFormat("de-CH", { style: "currency", currency: /^[A-Za-z]{3}$/.test(cur || "") ? cur.toUpperCase() : "CHF" }).format(Number(v || 0));
const EXAMPLE = "Müller Treuhand AG, Bahnhofstrasse 12, 8001 Zürich, info@mueller-treuhand.ch\n3 Stunden Personal Training à 120, Leistungszeitraum September 2026";

/** Schnellrechnung: Freitext → KI-Vorschau (editierbar) → Rechnung anlegen, buchen, PDF, E-Mail. */
export function QuickInvoiceDialog({ open, onOpenChange, companyId, onCreated }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const [done, setDone] = useState(null);

  const reset = () => { setText(""); setPv(null); setDone(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const parse = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/invoices/quick/parse`, { text });
      setPv(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Auswertung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const totals = pv ? (() => {
    const subtotal = pv.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const tax = pv.vat_rate ? subtotal * pv.vat_rate / 100 : 0;
    return { subtotal, tax, total: subtotal + tax };
  })() : null;

  const create = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/invoices/quick/create`, {
        customer: pv.customer, items: pv.items, vat_rate: pv.vat_rate, invoice_number: pv.invoice_number,
        issue_date: pv.issue_date, due_date: pv.due_date, currency: pv.currency, note: pv.note, source_text: text,
      });
      setDone(r.data);
      toast.success(`Rechnung ${r.data.invoice_number} erstellt und gebucht`);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const setCust = (k, v) => setPv({ ...pv, customer: { ...pv.customer, [k]: v } });
  const setItem = (idx, k, v) => setPv({ ...pv, items: pv.items.map((i, n) => (n === idx ? { ...i, [k]: v } : i)) });
  const openPdf = async () => {
    try {
      const r = await apiClient.get(done.pdf_url, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF konnte nicht geladen werden"); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quick-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Schnellrechnung</DialogTitle>
          <DialogDescription>
            Kunde, Adresse, was verkauft wurde und den Betrag in eigenen Worten schreiben – die KI erstellt die Rechnung
            {" "}mit oder ohne MWST (je nach Firma) und wählt das passende Ertragskonto für die Buchung.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3" data-testid="quick-invoice-done">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <p className="font-medium flex items-center gap-1.5"><Check className="h-4 w-4" /> Rechnung {done.invoice_number} über {fmt(done.total, done.currency)} erstellt und gebucht.</p>
              <p className="text-xs mt-1">{done.created_customer ? "Neuer Kunde wurde angelegt. " : ""}
                {done.emailed ? `Per E-Mail an ${done.recipient} gesendet.` : done.recipient ? `E-Mail an ${done.recipient} fehlgeschlagen: ${done.email_error}` : "Keine E-Mail-Adresse – Rechnung nicht versendet."}
                {" "}Die Zahlung wird im Bankabgleich automatisch dieser Rechnung zugeordnet.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={openPdf} variant="outline" data-testid="quick-invoice-pdf"><FileDown className="h-4 w-4 mr-1.5" /> PDF öffnen</Button>
              <Button onClick={() => { reset(); }} variant="outline" data-testid="quick-invoice-another"><Plus className="h-4 w-4 mr-1.5" /> Weitere Schnellrechnung</Button>
              <Button onClick={() => close(false)} data-testid="quick-invoice-close">Fertig</Button>
            </div>
          </div>
        ) : !pv ? (
          <div className="space-y-3">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={EXAMPLE} className="text-sm" data-testid="quick-invoice-text" />
            <div className="flex justify-between items-center">
              <button type="button" className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2" onClick={() => setText(EXAMPLE)} data-testid="quick-invoice-example">Beispiel einfügen</button>
              <Button onClick={parse} disabled={busy || text.trim().length < 8} data-testid="quick-invoice-parse">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Vorschau erstellen
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="quick-invoice-preview">
            <section className="rounded-md border border-slate-200 p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Kunde {pv.customer.existing_id ? <span className="text-emerald-700 normal-case">· bestehender Kunde gefunden</span> : <span className="text-amber-700 normal-case">· wird neu angelegt</span>}</p>
              <div className="grid grid-cols-2 gap-2">
                <Input value={pv.customer.name || ""} onChange={(e) => setCust("name", e.target.value)} placeholder="Name" className="col-span-2" data-testid="quick-invoice-customer-name" />
                <Input value={pv.customer.street || ""} onChange={(e) => setCust("street", e.target.value)} placeholder="Strasse" className="col-span-2" data-testid="quick-invoice-customer-street" />
                <Input value={pv.customer.postal_code || ""} onChange={(e) => setCust("postal_code", e.target.value)} placeholder="PLZ" data-testid="quick-invoice-customer-plz" />
                <Input value={pv.customer.city || ""} onChange={(e) => setCust("city", e.target.value)} placeholder="Ort" data-testid="quick-invoice-customer-city" />
                <Input value={pv.customer.email || ""} onChange={(e) => setCust("email", e.target.value)} placeholder="E-Mail (für Versand)" className="col-span-2" data-testid="quick-invoice-customer-email" />
              </div>
            </section>

            <section className="rounded-md border border-slate-200 p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Positionen <span className="normal-case">(Leistung · Menge · Preis netto · Ertragskonto)</span></p>
              <div className="space-y-2">
                {pv.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`quick-invoice-item-${idx}`}>
                    <Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="col-span-4" placeholder="Leistung" />
                    <Input type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="col-span-2" title="Menge" />
                    <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="col-span-2" title="Preis netto" />
                    <select value={it.account_id || ""} onChange={(e) => setItem(idx, "account_id", e.target.value)} className="col-span-3 h-9 rounded-md border border-slate-300 bg-white px-1 text-xs" data-testid={`quick-invoice-item-account-${idx}`}>
                      {pv.revenue_accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                      {!pv.revenue_accounts.some((a) => a.code === it.account_id) && it.account_id && <option value={it.account_id}>{it.account_id}</option>}
                    </select>
                    <button type="button" onClick={() => setPv({ ...pv, items: pv.items.filter((_, n) => n !== idx) })} className="col-span-1 text-slate-300 hover:text-rose-600 justify-self-center"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setPv({ ...pv, items: [...pv.items, { description: "", quantity: 1, unit_price: 0, account_id: pv.items[0]?.account_id }] })} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Position</button>
              </div>
              <div className="mt-3 text-sm text-right space-y-0.5" data-testid="quick-invoice-totals">
                <p className="text-slate-600">Netto {fmt(totals.subtotal, pv.currency)}</p>
                <p className="text-slate-600">{pv.vat_applies ? `MWST ${pv.vat_rate}% ${fmt(totals.tax, pv.currency)}` : "Ohne MWST (Firma nicht MWST-pflichtig)"}</p>
                <p className="font-semibold text-slate-900">Total {fmt(totals.total, pv.currency)}</p>
              </div>
            </section>

            <section className="grid grid-cols-3 gap-2 text-xs">
              <label className="space-y-1"><span className="text-slate-500">Rechnungs-Nr.</span><Input value={pv.invoice_number} onChange={(e) => setPv({ ...pv, invoice_number: e.target.value })} data-testid="quick-invoice-number" /></label>
              <label className="space-y-1"><span className="text-slate-500">Datum</span><SwissDateField  value={pv.issue_date} onChange={(e) => setPv({ ...pv, issue_date: e.target.value })} /></label>
              <label className="space-y-1"><span className="text-slate-500">Fällig</span><SwissDateField  value={pv.due_date} onChange={(e) => setPv({ ...pv, due_date: e.target.value })} /></label>
            </section>
            {pv.booking_hint && <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5" data-testid="quick-invoice-booking-hint"><span className="font-medium">Buchung:</span> {pv.booking_hint}</p>}

            <div className="flex justify-between items-center pt-1">
              <Button variant="ghost" onClick={() => setPv(null)} disabled={busy} data-testid="quick-invoice-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Text ändern</Button>
              <Button onClick={create} disabled={busy || !pv.customer.name || !pv.items.length} className="bg-slate-900" data-testid="quick-invoice-create">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
                Erstellen, buchen{pv.customer.email ? " & senden" : ""}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
